import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Briefcase, MapPin, Search, Shield, SlidersHorizontal, Target } from 'lucide-react';
import type { QuestionnaireAnswers } from '@nemo/core/talent/questionnaire';
import { formatCount, formatDay, formatYears } from '../ui/format.js';
import { ActiveFilters } from '../ui/FilterDialog.js';
import { saidNoService, shortAnswer } from './answers.js';
import { CandidatePane } from './CandidatePane.js';
import { CandidateTile } from './CandidateTile.js';
import { IconFact } from './IconFact.js';
import {
  NO_TALENT_FILTERS,
  TalentFilterDialog,
  talentFilterChips,
  type TalentFilters,
} from './TalentFilters.js';
import { useTalentTRPC } from './trpc.js';

/** How long a person has to stop typing before the search asks the api. */
const SEARCH_DEBOUNCE_MS = 300;

/** The row names this many positions sought, then counts the rest. */
const VISIBLE_POSITIONS = 2;

/** The typed filters wait for a pause in typing together, so one pause is one search. */
function useDebounced<T>(value: T): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [value]);
  return settled;
}

/** The row's second line: where they are, how long they have worked, service, what they want. */
function RowFacts(props: {
  candidate: {
    location: string | null;
    yearsExperience: number | null;
    answers: QuestionnaireAnswers;
    submittedAt: string;
  };
}): React.JSX.Element {
  const { candidate } = props;
  const branch = saidNoService(candidate.answers)
    ? 'No service'
    : (shortAnswer(candidate.answers, 'branch')?.values[0] ?? null);
  const positions = shortAnswer(candidate.answers, 'positions')?.values ?? [];
  const hidden = positions.length - VISIBLE_POSITIONS;
  const sought =
    positions.length === 0
      ? null
      : positions.slice(0, VISIBLE_POSITIONS).join(', ') + (hidden > 0 ? ` +${hidden}` : '');
  return (
    <span className="talent-row-facts">
      <IconFact icon={MapPin} label="Location" text={candidate.location} />
      <IconFact
        icon={Briefcase}
        label="Experience"
        text={candidate.yearsExperience === null ? null : formatYears(candidate.yearsExperience)}
      />
      <IconFact icon={Shield} label="Service" text={branch} />
      <IconFact icon={Target} label="Looking for" text={sought} />
      <span className="talent-row-date">{formatDay(candidate.submittedAt)}</span>
    </span>
  );
}

export function TalentPage(): React.JSX.Element {
  const trpc = useTalentTRPC();
  const [text, setText] = useState('');
  const [filters, setFilters] = useState<TalentFilters>(NO_TALENT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  // Memoised: a fresh object each render would restart the debounce timer forever.
  const fields = useMemo(
    () => ({
      text: text.trim(),
      location: filters.location.trim(),
      skill: filters.skill.trim(),
      school: filters.school.trim(),
      language: filters.language.trim(),
    }),
    [text, filters.location, filters.skill, filters.school, filters.language],
  );
  const typed = useDebounced(fields);

  const search = useQuery(
    trpc.talent.search.queryOptions(
      {
        ...(typed.text === '' ? {} : { text: typed.text }),
        ...(typed.location === '' ? {} : { location: typed.location }),
        ...(typed.skill === '' ? {} : { skill: typed.skill }),
        ...(filters.minYears === '' ? {} : { minYears: Number(filters.minYears) }),
        ...(typed.school === '' ? {} : { school: typed.school }),
        ...(filters.minYearsServed === ''
          ? {}
          : { minYearsServed: Number(filters.minYearsServed) }),
        ...(typed.language === '' ? {} : { language: typed.language }),
        ...(Object.keys(filters.answers).length === 0 ? {} : { answers: filters.answers }),
      },
      { placeholderData: keepPreviousData },
    ),
  );
  const closePane = useCallback(() => setOpenId(null), []);

  const chips = useMemo(() => talentFilterChips(filters), [filters]);
  const candidates = search.data?.candidates ?? [];
  return (
    <div className="page">
      <div className="toolbar">
        <strong className="count">
          {search.data === undefined
            ? 'Candidates'
            : `${formatCount(search.data.total)} candidate${search.data.total === 1 ? '' : 's'}`}
        </strong>
        <span className="field-with-icon">
          <Search aria-hidden="true" />
          <input
            className="search"
            placeholder="Search the CVs…"
            aria-label="Search the CVs"
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </span>
        <button
          className="button"
          type="button"
          aria-haspopup="dialog"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen(true)}
        >
          <SlidersHorizontal aria-hidden="true" />
          Filters
          {chips.length > 0 && <span className="filter-count">{chips.length}</span>}
        </button>
      </div>
      <ActiveFilters
        chips={chips}
        onChange={setFilters}
        onClearAll={() => setFilters(NO_TALENT_FILTERS)}
      />
      {filtersOpen && (
        <TalentFilterDialog
          filters={filters}
          matching={search.data?.total ?? 0}
          onChange={setFilters}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      <div className="talent-body">
        <div className="talent-main">
          {search.isError && (
            <p className="subtle">Could not load the candidates: {search.error.message}</p>
          )}
          {search.data?.textIgnored === true && (
            <p className="subtle">The search words were ignored (only common words)</p>
          )}
          {!search.isPending && !search.isError && candidates.length === 0 && (
            <p className="subtle">No candidates match.</p>
          )}
          <ul className="talent-results">
            {candidates.map((candidate) => (
              <li key={candidate.id}>
                <button
                  className="talent-row talent-row-with-tile"
                  type="button"
                  aria-pressed={candidate.id === openId}
                  onClick={() => setOpenId(candidate.id)}
                >
                  <CandidateTile key={candidate.id} candidate={candidate} />
                  <span className="talent-row-text">
                    <span className="talent-row-title">
                      <strong>{candidate.name}</strong>
                      {candidate.headline !== null && (
                        <span className="talent-row-headline">{candidate.headline}</span>
                      )}
                    </span>
                    <RowFacts candidate={candidate} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        {openId !== null && <CandidatePane key={openId} id={openId} onClose={closePane} />}
      </div>
    </div>
  );
}
